// covers the step's 300ms enter fade so the bottom is seen before the glide
const REVEAL_PAUSE_MS = 500
const REVEAL_MIN_DISTANCE = 48
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const

/** The flow panel scrolls on wide layouts; narrow layouts scroll the page instead. */
function getStepScroller(): { scroller: Element, top: number } | null {
  const flowMain = document.querySelector('.flow-main')
  if (flowMain && /^(auto|scroll)$/.test(window.getComputedStyle(flowMain).overflowY))
    return { scroller: flowMain, top: 0 }

  const page = document.scrollingElement
  if (!page)
    return null

  // start at the progress header rather than back up on the brand panel
  const flowPanel = document.querySelector('.flow-panel')
  const flowPanelTop = flowPanel
    ? flowPanel.getBoundingClientRect().top + page.scrollTop
    : 0
  return { scroller: page, top: Math.max(0, flowPanelTop) }
}

function easeInOutCubic(progress: number): number {
  return progress < 0.5
    ? 4 * progress ** 3
    : 1 - (-2 * progress + 2) ** 3 / 2
}

/**
 * Put a newly shown step at its top. With reveal, first show the bottom and glide up so the
 * length of the step is visible; any user scroll or input stops the glide where it is.
 * Returns a cancel function.
 */
export function scrollStepIntoView(reveal: boolean): () => void {
  const target = getStepScroller()
  if (!target)
    return () => {}

  const { scroller, top } = target
  const bottom = scroller.scrollHeight - scroller.clientHeight
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (!reveal || reduceMotion || bottom - top < REVEAL_MIN_DISTANCE) {
    scroller.scrollTop = Math.min(top, bottom)
    return () => {}
  }

  scroller.scrollTop = bottom
  const distance = bottom - top
  const duration = Math.min(1100, Math.max(500, distance * 0.6))
  let animationFrame: number | undefined
  const pauseTimer = window.setTimeout(() => {
    const start = performance.now()
    const step = (now: number) => {
      const progress = Math.min(1, (now - start) / duration)
      scroller.scrollTop = bottom - distance * easeInOutCubic(progress)
      if (progress < 1)
        animationFrame = window.requestAnimationFrame(step)
      else
        cancel()
    }
    animationFrame = window.requestAnimationFrame(step)
  }, REVEAL_PAUSE_MS)

  function cancel() {
    window.clearTimeout(pauseTimer)
    if (animationFrame !== undefined) window.cancelAnimationFrame(animationFrame)
    for (const type of USER_SCROLL_EVENTS)
      window.removeEventListener(type, cancel)
  }

  for (const type of USER_SCROLL_EVENTS)
    window.addEventListener(type, cancel, { passive: true })

  return cancel
}
